// Frontend entry - Bootstrap 5 Full JS Bundle
import "../css/styles.scss";

// Import full Bootstrap bundle (includes Popper.js)
import bs from "./common";

import {
  fieldNrTeczki,
  initReadonlySelectPreviews,
  autoFillField,
  cleanEmptyGetForms,
  initAdvancedFilterToggles,
  initZeroNumberInputSelect,
  initAutoSubmitFilterForms,
  initConfirmForms,
  initTableBulkForms,
  initProgressiveFields,
  inlineSearch,
  sanitizeFormFields,
} from "./custom/form";
import { initTableScrollbars, stickyTableColumns, stickyTableHeader } from "./custom/helpers";
import SpnTable from "./custom/spn";
import { initDynamicModals } from "./custom/dynamicModal";
import { initSpnSelect2, initSelect2 } from "./custom/enhancedSelects";
import { initGroupAssignments, initGroupContextFilters, initGroupCreateContextPicker } from "./custom/groupAssignments";
import { initSummaryGroups } from "./custom/summaryGroups";
import { initTableCrossHover } from "./custom/tableCrossHover";
import { initGroupTableGroupFocus } from "./custom/groupTableGroupFocus";
import { initGroupCapacityRules } from "./custom/groupCapacityRules";
import { initDismissibleAlerts } from "./custom/dismissibleAlerts";
import { initReportProblemWidget } from "./custom/reportProblemWidget";
import { initSchoolClassSpnAssignments } from "./custom/schoolClassSpnAssignments";
import { initSchoolClassForms } from "./custom/schoolClassForm";
import { initGroupForms } from "./custom/groupForm";
import { initSheetFiles } from "./custom/sheetFiles";
import { initSheetEffectiveDates } from "./custom/sheetEffectiveDate";
import { initSheetChangesLineage } from "./custom/sheetChangesLineage";
import { initSheetInfoMissingFields } from "./custom/sheetInfoMissingFields";
import { initSheetSubmissionModal } from "./custom/sheetSubmissionModal";
import { initSpnTableScrollSpy } from "./custom/spnTableScrollSpy";
import { applyStudentNameMasks } from "./custom/studentNameMasks";
import { initStudentMaskExportLinks } from "./custom/studentMaskExportLinks";
import { initStudentMaskSearch } from "./custom/studentMaskSearch.js";
import { initStudentIdentifierValidation } from "./custom/studentIdentifierValidation";
import { initStudentSpnFilters } from "./custom/studentSpnFilters";
import { getStudentIdentityStorageEvent, getStudentIdentityStorageKey } from "./custom/studentIdentityStore";
import { initLoginSubmitLock } from "./custom/loginSubmitLock";
import { initMessageReplyEditors } from "./custom/messageReplyEditor";
import { initMessageThreadSidebar } from "./custom/messageThreadSidebar";
import { initMessageCompose } from "./custom/messageCompose";
import { initTwoFactorAutoSubmit } from "./custom/twoFactorAutoSubmit";
import { initLazyVueApps } from "./vue/lazyApps";

const GROUP_ASSIGNMENTS_SELECTOR = ".js-group-assignments";
const GROUP_ASSIGNMENTS_OBSERVED_ATTRIBUTE = "data-group-assignments-lazy-observed";
const GROUP_ASSIGNMENTS_LOADING_ATTRIBUTE = "data-group-assignments-lazy-loading";
const LAZY_COMPONENT_ROOT_MARGIN = "200px 0px";

let groupAssignmentsObserver = null;

function collectElements(root, selector) {
  const elements = [];

  if (root instanceof Element && root.matches(selector)) {
    elements.push(root);
  }

  if (root instanceof Document || root instanceof Element) {
    elements.push(...root.querySelectorAll(selector));
  }

  return Array.from(new Set(elements));
}

function supportsIntersectionObserver() {
  return typeof window !== "undefined" && "IntersectionObserver" in window;
}

async function mountGroupAssignments(element) {
  if (!(element instanceof Element)) {
    return;
  }

  if (
    element.dataset.groupAssignmentsInitialized === "1" ||
    element.hasAttribute(GROUP_ASSIGNMENTS_LOADING_ATTRIBUTE)
  ) {
    return;
  }

  element.setAttribute(GROUP_ASSIGNMENTS_LOADING_ATTRIBUTE, "1");
  element.setAttribute("aria-busy", "true");

  try {
    initGroupAssignments(element);
  } catch (error) {
    element.removeAttribute(GROUP_ASSIGNMENTS_OBSERVED_ATTRIBUTE);
    console.error('Failed to lazy-mount "group assignments".', error);
  } finally {
    element.removeAttribute(GROUP_ASSIGNMENTS_LOADING_ATTRIBUTE);
    element.removeAttribute("aria-busy");
  }
}

function getGroupAssignmentsObserver() {
  if (groupAssignmentsObserver || !supportsIntersectionObserver()) {
    return groupAssignmentsObserver;
  }

  groupAssignmentsObserver = new window.IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        groupAssignmentsObserver?.unobserve(entry.target);
        mountGroupAssignments(entry.target);
      });
    },
    {
      root: null,
      rootMargin: LAZY_COMPONENT_ROOT_MARGIN,
      threshold: 0.01,
    },
  );

  return groupAssignmentsObserver;
}

function initLazyGroupAssignments(root = document, options = {}) {
  const eager = options?.eager === true;

  collectElements(root, GROUP_ASSIGNMENTS_SELECTOR).forEach((element) => {
    if (
      element.dataset.groupAssignmentsInitialized === "1" ||
      element.hasAttribute(GROUP_ASSIGNMENTS_LOADING_ATTRIBUTE)
    ) {
      return;
    }

    if (eager || !supportsIntersectionObserver()) {
      mountGroupAssignments(element);
      return;
    }

    if (element.hasAttribute(GROUP_ASSIGNMENTS_OBSERVED_ATTRIBUTE)) {
      return;
    }

    element.setAttribute(GROUP_ASSIGNMENTS_OBSERVED_ATTRIBUTE, "1");
    getGroupAssignmentsObserver()?.observe(element);
  });
}

// Initialize Bootstrap components when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  initReadonlySelectPreviews(document);
  bs.BS5Init();
  new bs.BS5Validator();
  fieldNrTeczki();
  stickyTableHeader();
  initTableScrollbars();
  stickyTableColumns();
  autoFillField();
  sanitizeFormFields();
  initZeroNumberInputSelect(document);
  initGroupContextFilters(document);
  initGroupCreateContextPicker(document);
  cleanEmptyGetForms(document);
  initAutoSubmitFilterForms(document);
  initAdvancedFilterToggles(document);
  initTableBulkForms(document);
  initConfirmForms(document);
  initProgressiveFields(document);
  inlineSearch();
  initDynamicModals();
  initLazyGroupAssignments(document);
  initGroupTableGroupFocus(document);
  initSummaryGroups(document);
  initTableCrossHover(document);
  initDismissibleAlerts(document);
  initReportProblemWidget(document);
  initSchoolClassSpnAssignments(document);
  initSchoolClassForms(document);
  initSheetFiles(document);
  initSheetEffectiveDates(document);
  initSheetChangesLineage(document);
  initSheetInfoMissingFields(document);
  initSheetSubmissionModal(document);
  initSpnSelect2(document);
  initSelect2(".js-select");
  initGroupForms(document);
  initMessageCompose(document);
  initGroupCapacityRules(document);
  initLazyVueApps(document);
  initSpnTableScrollSpy(document);
  applyStudentNameMasks(document);
  initStudentMaskSearch(document);
  initStudentMaskExportLinks(document);
  initStudentIdentifierValidation(document);
  initStudentSpnFilters(document);
  initMessageReplyEditors(document);
  initMessageThreadSidebar(document);
  initLoginSubmitLock(document);
  initTwoFactorAutoSubmit(document);

  new SpnTable();
});

// Re-initialize Bootstrap components when new content is dynamically added
document.addEventListener("htmx:afterSwap", () => {
  initReadonlySelectPreviews(document);
  bs.BS5Init();
  new bs.BS5Validator();
  fieldNrTeczki();
  initTableScrollbars();
  stickyTableColumns();
  autoFillField();
  sanitizeFormFields();
  initZeroNumberInputSelect(document);
  initGroupContextFilters(document);
  initGroupCreateContextPicker(document);
  cleanEmptyGetForms(document);
  initAutoSubmitFilterForms(document);
  initAdvancedFilterToggles(document);
  initTableBulkForms(document);
  initConfirmForms(document);
  initProgressiveFields(document);
  initDismissibleAlerts(document);
  initReportProblemWidget(document);
  initLazyGroupAssignments(document);
  initGroupTableGroupFocus(document);
  initSummaryGroups(document);
  initTableCrossHover(document);
  initSchoolClassSpnAssignments(document);
  initSchoolClassForms(document);
  initSheetFiles(document);
  initSheetEffectiveDates(document);
  initSpnSelect2(document);
  initSelect2(document, ".js-select");
  initGroupForms(document);
  initGroupCapacityRules(document);
  initLazyVueApps(document);
  initSpnTableScrollSpy(document);
  applyStudentNameMasks(document);
  initStudentMaskSearch(document);
  initStudentMaskExportLinks(document);
  initStudentIdentifierValidation(document);
  initStudentSpnFilters(document);
  initMessageReplyEditors(document);
  initMessageThreadSidebar(document);
  initLoginSubmitLock(document);
});

document.addEventListener("cea:dynamic-modal:loaded", (e) => {
  const modalEl = e?.detail?.modalEl;
  if (modalEl) {
    initReadonlySelectPreviews(modalEl);
    fieldNrTeczki(modalEl);
    initSpnSelect2(modalEl);
    initSelect2(modalEl, ".js-select");
    initGroupForms(modalEl);
    initTableScrollbars(modalEl);
    sanitizeFormFields();
    autoFillField();
    initZeroNumberInputSelect(modalEl);
    cleanEmptyGetForms(modalEl);
    initAutoSubmitFilterForms(modalEl);
    initAdvancedFilterToggles(modalEl);
    initTableBulkForms(modalEl);
    initConfirmForms(modalEl);
    initProgressiveFields(modalEl);
    initSchoolClassSpnAssignments(modalEl);
    initSchoolClassForms(modalEl);
    initSheetFiles(modalEl);
    initLazyVueApps(modalEl, { eager: true });
    applyStudentNameMasks(modalEl);
    initStudentMaskExportLinks(modalEl);
    initStudentIdentifierValidation(modalEl);
    initStudentSpnFilters(modalEl);
  }
});

document.addEventListener("shown.bs.tab", (event) => {
  const trigger = event?.target instanceof Element ? event.target : null;
  const targetSelector = trigger?.getAttribute("data-bs-target") || trigger?.getAttribute("href") || "";
  if (!targetSelector || !targetSelector.startsWith("#")) {
    return;
  }

  const tabPane = document.querySelector(targetSelector);
  if (tabPane instanceof Element) {
    initTableScrollbars(tabPane);
    stickyTableColumns(tabPane);
    initLazyGroupAssignments(tabPane);
    initLazyVueApps(tabPane);
  }
});

window.addEventListener(getStudentIdentityStorageEvent(), () => {
  applyStudentNameMasks(document);
});

window.addEventListener("storage", (event) => {
  if (event.key === getStudentIdentityStorageKey()) {
    applyStudentNameMasks(document);
  }
});

export const BS5Init = bs.BS5Init;

console.log("cea.arkusze-new.local - frontend initialized");
